import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

// ============================================================================
// TYPES
// ============================================================================

export interface DeliveryAssignment {
  id: string;
  order_id: string;
  driver_id: string;
  assignment_status: 'assigned' | 'accepted' | 'picked_up' | 'in_transit' | 'delivered' | 'failed' | 'cancelled';
  assigned_at: string;
  accepted_at?: string;
  picked_up_at?: string;
  in_transit_at?: string;
  delivered_at?: string;
  failed_at?: string;
  cancelled_at?: string;
  estimated_delivery_time_minutes?: number;
  actual_delivery_time_minutes?: number;
  failure_reason?: string;
  cancellation_reason?: string;
  driver_earnings: number;
  delivery_fee: number;
  customer_rating?: number;
  customer_feedback?: string;
  delivery_proof_url?: string;
  delivery_signature_url?: string;
  notes?: string;
  rider_notes?: string;
  created_at: string;
  updated_at: string;
  order?: any; // Full order object with items
}

// ============================================================================
// HOOK: useRiderOrders
// ============================================================================

/**
 * Custom hook to fetch and manage orders assigned to a specific rider
 * Only fetches orders assigned to the current rider (data isolation)
 */
export function useRiderOrders(riderId: string | undefined) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch rider's active deliveries
  const {
    data: activeAssignments = [],
    isLoading,
    error,
    refetch
  } = useQuery<DeliveryAssignment[]>({
    queryKey: ['riderOrders', riderId, 'active'],
    queryFn: async () => {
      if (!riderId) return [];

      const { data, error } = await supabase
        .from('delivery_assignments')
        .select(`
          *,
          order:orders (
            id,
            order_number,
            customer_name,
            customer_phone,
            customer_email,
            customer_address,
            total_amount,
            order_type,
            status,
            notes,
            created_at,
            branch_id,
            order_items (
              id,
              quantity,
              unit_price,
              total_price,
              menu_items (
                id,
                name,
                description,
                image_url
              )
            ),
            delivery_zone:delivery_zones (
              id,
              zone_name,
              delivery_fee,
              estimated_delivery_time_minutes
            )
          )
        `)
        .eq('driver_id', riderId)
        .in('assignment_status', ['assigned', 'accepted', 'picked_up', 'in_transit'])
        .order('assigned_at', { ascending: false });

      if (error) {
        console.error('Error fetching rider orders:', error);
        throw error;
      }

      return data || [];
    },
    enabled: !!riderId,
    refetchInterval: 10000, // Refresh every 10 seconds
    staleTime: 5000
  });

  // Fetch rider's delivery history
  const {
    data: completedAssignments = [],
    isLoading: isLoadingHistory
  } = useQuery<DeliveryAssignment[]>({
    queryKey: ['riderOrders', riderId, 'history'],
    queryFn: async () => {
      if (!riderId) return [];

      const { data, error } = await supabase
        .from('delivery_assignments')
        .select(`
          *,
          order:orders (
            id,
            order_number,
            customer_name,
            customer_phone,
            customer_address,
            total_amount,
            order_type,
            created_at
          )
        `)
        .eq('driver_id', riderId)
        .in('assignment_status', ['delivered', 'failed', 'cancelled'])
        .order('delivered_at', { ascending: false })
        .limit(50);

      if (error) {
        console.error('Error fetching rider history:', error);
        throw error;
      }

      return data || [];
    },
    enabled: !!riderId,
    staleTime: 60000 // History updates less frequently
  });

  // Mutation to update assignment status
  const updateAssignmentStatus = useMutation({
    mutationFn: async ({
      assignmentId,
      newStatus,
      notes
    }: {
      assignmentId: string;
      newStatus: DeliveryAssignment['assignment_status'];
      notes?: string;
    }) => {
      if (!riderId) throw new Error('Rider ID is required');

      const updates: any = {
        assignment_status: newStatus,
        updated_at: new Date().toISOString()
      };

      // Add timestamp fields based on status
      const now = new Date().toISOString();
      if (newStatus === 'accepted') {
        updates.accepted_at = now;
      } else if (newStatus === 'picked_up') {
        updates.picked_up_at = now;
      } else if (newStatus === 'in_transit') {
        updates.in_transit_at = now;
      } else if (newStatus === 'delivered') {
        updates.delivered_at = now;
      } else if (newStatus === 'failed') {
        updates.failed_at = now;
        if (notes) updates.failure_reason = notes;
      } else if (newStatus === 'cancelled') {
        updates.cancelled_at = now;
        if (notes) updates.cancellation_reason = notes;
      }

      if (notes && !updates.failure_reason && !updates.cancellation_reason) {
        updates.rider_notes = notes;
      }

      // Update assignment
      const { data, error } = await supabase
        .from('delivery_assignments')
        .update(updates)
        .eq('id', assignmentId)
        .eq('driver_id', riderId) // Ensure rider owns this assignment
        .select()
        .single();

      if (error) throw error;

      // If delivered or failed, update order status
      if (newStatus === 'delivered') {
        await supabase
          .from('orders')
          .update({ status: 'completed' })
          .eq('id', data.order_id);
      }

      return data;
    },
    onMutate: async (variables) => {
      // Cancel outgoing refetches so they don't overwrite optimistic update
      await queryClient.cancelQueries({ queryKey: ['riderOrders', riderId, 'active'] });

      // Get previous data for rollback
      const previousData = queryClient.getQueryData<DeliveryAssignment[]>(['riderOrders', riderId, 'active']);

      // Optimistically update active assignments
      queryClient.setQueryData<DeliveryAssignment[]>(
        ['riderOrders', riderId, 'active'],
        (old) => {
          if (!old) return old;
          return old.map(assignment =>
            assignment.id === variables.assignmentId
              ? { ...assignment, assignment_status: variables.newStatus }
              : assignment
          );
        }
      );

      return { previousData };
    },
    onSuccess: (data, variables) => {
      // Only invalidate the stats query, not the full list
      queryClient.invalidateQueries({ queryKey: ['riderStats', riderId] });
      
      const statusMessages: Record<string, string> = {
        accepted: 'Order accepted successfully',
        picked_up: 'Order marked as picked up',
        in_transit: 'Delivery in progress',
        delivered: 'Order delivered successfully!',
        failed: 'Delivery marked as failed',
        cancelled: 'Delivery cancelled'
      };

      toast({
        title: 'Status Updated',
        description: statusMessages[variables.newStatus] || 'Status updated',
      });
    },
    onError: (error: Error, variables: { assignmentId: string; newStatus: DeliveryAssignment['assignment_status']; notes?: string }, context: { previousData?: DeliveryAssignment[] } | undefined) => {
      console.error('Error updating assignment status:', error);
      
      // Rollback optimistic update
      if (context?.previousData) {
        queryClient.setQueryData(['riderOrders', riderId, 'active'], context.previousData);
      }

      toast({
        title: 'Error',
        description: error.message || 'Failed to update status',
        variant: 'destructive'
      });
    }
  });

  // Calculate statistics for the rider
  const statistics = {
    active: activeAssignments.length,
    assigned: activeAssignments.filter(a => a.assignment_status === 'assigned').length,
    inProgress: activeAssignments.filter(a => 
      ['accepted', 'picked_up', 'in_transit'].includes(a.assignment_status)
    ).length,
    todayCompleted: completedAssignments.filter(a => {
      if (!a.delivered_at) return false;
      const today = new Date().toDateString();
      return new Date(a.delivered_at).toDateString() === today;
    }).length,
    todayEarnings: completedAssignments
      .filter(a => {
        if (!a.delivered_at) return false;
        const today = new Date().toDateString();
        return new Date(a.delivered_at).toDateString() === today;
      })
      .reduce((sum, a) => sum + (a.driver_earnings || 0), 0),
    averageRating: completedAssignments
      .filter(a => a.customer_rating)
      .reduce((sum, a, _, arr) => sum + (a.customer_rating || 0) / arr.length, 0) || 0,
    totalDeliveries: completedAssignments.filter(a => a.assignment_status === 'delivered').length
  };

  return {
    activeAssignments,
    completedAssignments,
    statistics,
    isLoading,
    isLoadingHistory,
    error,
    refetch,
    updateAssignmentStatus: updateAssignmentStatus.mutate,
    isUpdating: updateAssignmentStatus.isPending
  };
}

// ============================================================================
// HOOK: useRiderStats
// ============================================================================

/**
 * Hook to fetch detailed rider statistics and performance metrics
 */
export function useRiderStats(riderId: string | undefined) {
  return useQuery({
    queryKey: ['riderStats', riderId],
    queryFn: async () => {
      if (!riderId) return null;

      // Get today's date range
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      // Fetch today's deliveries
      const { data: todayDeliveries, error: todayError } = await supabase
        .from('delivery_assignments')
        .select('*')
        .eq('driver_id', riderId)
        .eq('assignment_status', 'delivered')
        .gte('delivered_at', today.toISOString())
        .lt('delivered_at', tomorrow.toISOString());

      if (todayError) throw todayError;

      // Fetch all-time stats
      const { data: allTimeStats, error: statsError } = await supabase
        .from('delivery_assignments')
        .select('*')
        .eq('driver_id', riderId)
        .in('assignment_status', ['delivered', 'failed']);

      if (statsError) throw statsError;

      const delivered = allTimeStats?.filter(a => a.assignment_status === 'delivered') || [];
      const failed = allTimeStats?.filter(a => a.assignment_status === 'failed') || [];

      // Calculate average rating correctly
      const ratingsArray = delivered.filter(a => a.customer_rating && a.customer_rating > 0);
      const averageRating = ratingsArray.length > 0
        ? ratingsArray.reduce((sum, a) => sum + (a.customer_rating || 0), 0) / ratingsArray.length
        : 0;

      return {
        today: {
          deliveries: todayDeliveries?.length || 0,
          earnings: todayDeliveries?.reduce((sum, a) => sum + (a.driver_earnings || 0), 0) || 0,
          averageTime: todayDeliveries?.length
            ? todayDeliveries.reduce((sum, a) => sum + (a.actual_delivery_time_minutes || 0), 0) / todayDeliveries.length
            : 0
        },
        allTime: {
          totalDeliveries: delivered.length,
          failedDeliveries: failed.length,
          successRate: delivered.length + failed.length > 0
            ? (delivered.length / (delivered.length + failed.length)) * 100
            : 0,
          totalEarnings: delivered.reduce((sum, a) => sum + (a.driver_earnings || 0), 0),
          averageRating: averageRating
        }
      };
    },
    enabled: !!riderId,
    staleTime: 30000, // Refresh every 30 seconds for real-time earnings updates
    refetchInterval: 30000
  });
}
